import { QR_ENTITY_TYPES } from '../../../generic/qr/qrEntityTypes'
import { QR_ACTIONS } from './qrLookupApi'

/**
 * The keys a QR Lookup widget saves, with the value each one takes when it is not saved.
 *
 * One table for the gear and for the widget on purpose: the gear seeds new widgets from it and the
 * widget reads saved ones through it, so a widget saved before a key existed behaves as that key's
 * default — which is how "the configuration of the existing widgets does not change" holds.
 */

// Gear order (RN2): the four original fields and the direct reader — the "Reading" section — then the
// action. Amendment 6 changes how they are drawn (RN31), never their keys or their defaults.
export const QR_READING_FIELDS = [
  {
    key: 'expectedEntityType',
    type: 'select',
    label: 'Expected type',
    defaultValue: 'Any',
    selectableOptions: ['Any', ...QR_ENTITY_TYPES]
  },
  {
    key: 'initialDocumentScope',
    type: 'select',
    label: 'Documents shown first',
    defaultValue: 'Reference',
    selectableOptions: ['Reference', 'Instance']
  },
  { key: 'allowCamera', type: 'boolean', label: 'Allow camera', defaultValue: true },
  { key: 'allowManual', type: 'boolean', label: 'Allow manual entry', defaultValue: true },
  // Off by default and with either action: it decides how a label is read, not what opens.
  { key: 'listenWithoutDialog', type: 'boolean', label: 'Listen to the reader without opening the dialog', defaultValue: false }
]

// Drawn as two option cards, each saying what a read will open.
export const QR_ACTION_FIELD = {
  key: 'action',
  type: 'select',
  label: 'Action upon scanning',
  defaultValue: QR_ACTIONS.SHEET,
  selectableOptions: [QR_ACTIONS.SHEET, QR_ACTIONS.JOB_DETAIL],
  optionLabels: { [QR_ACTIONS.SHEET]: 'Element sheet', [QR_ACTIONS.JOB_DETAIL]: 'Job detail' },
  optionHelp: {
    [QR_ACTIONS.SHEET]: 'Drawings and documents of whatever was read',
    [QR_ACTIONS.JOB_DETAIL]: 'For an FO or part, the job of that order for this machine'
  }
}

export const QR_LOOKUP_FIELDS = [...QR_READING_FIELDS, QR_ACTION_FIELD]

export const JOB_ORDER_CRITERIA = { PLAN: 'PLAN', PRIORITY: 'PRIORITY' }

// The block "How the job is chosen": shown, and applied, only with the JOB_DETAIL action. Labelled in
// the plant's words, each with a help line (RN31). suggestTargetDevice is the front's own (it only
// shows or hides "Go to"); the other three travel to Sun with every JOB_DETAIL read.
export const QR_JOB_DETAIL_FIELDS = [
  {
    key: 'jobOrderCriterion',
    type: 'select',
    label: 'Order of the jobs',
    help: 'When there are several, first',
    defaultValue: JOB_ORDER_CRITERIA.PLAN,
    selectableOptions: [JOB_ORDER_CRITERIA.PLAN, JOB_ORDER_CRITERIA.PRIORITY],
    // Not the existing 'Plan' key: in Spanish it reads as the verb.
    optionLabels: { [JOB_ORDER_CRITERIA.PLAN]: 'The next one in the plan', [JOB_ORDER_CRITERIA.PRIORITY]: 'By priority' }
  },
  {
    key: 'allowTechnologyFallback',
    type: 'boolean',
    label: 'Also jobs this machine can do',
    help: 'If nothing is planned here, open one that matches its technologies',
    defaultValue: true
  },
  {
    key: 'suggestTargetDevice',
    type: 'boolean',
    label: 'Button to the right machine',
    help: 'When the work is elsewhere, offer to jump to that screen',
    defaultValue: true
  },
  {
    key: 'askOperatorOnMultipleJobs',
    type: 'boolean',
    label: 'Let the operator choose',
    help: 'Show the list instead of opening the first one',
    defaultValue: false
  }
]

const fieldDefaults = (fields) =>
  fields.reduce((defaults, field) => ({ ...defaults, [field.key]: field.defaultValue }), {})

/** What a saved configuration means, with every missing key at its default. */
export const readQrLookupConfig = (data = {}) => ({
  ...fieldDefaults(QR_LOOKUP_FIELDS),
  ...fieldDefaults(QR_JOB_DETAIL_FIELDS),
  ...Object.fromEntries(Object.entries(data ?? {}).filter(([, value]) => value !== undefined && value !== null))
})
